import React, { useState, useEffect } from 'react';
import { chaptersApi, scenesApi } from '../../lib/api';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Badge } from '../ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { toast } from 'sonner';
import {
    DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors,
    DragOverlay
} from '@dnd-kit/core';
import {
    arrayMove, SortableContext, sortableKeyboardCoordinates, 
    rectSortingStrategy, useSortable
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
    LayoutGrid, Plus, GripVertical, FileText, Edit2, Trash2, 
    ChevronRight, Eye, Maximize2, X
} from 'lucide-react';

// Individual Card Component
const CorkboardCard = ({ item, type, onEdit, onDelete, onSelect, isSelected }) => {
    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
        isDragging
    } = useSortable({ id: item.id });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
    };

    const wordCount = item.word_count || 0;
    const synopsis = item.synopsis || item.description || '';

    return (
        <div
            ref={setNodeRef}
            style={style}
            className={`
                card-default p-0 overflow-hidden cursor-pointer transition-all
                hover:ring-2 hover:ring-accent/50 hover:shadow-lg
                ${isSelected ? 'ring-2 ring-accent shadow-lg' : ''}
                ${isDragging ? 'z-50' : ''}
            `}
            onClick={() => onSelect(item)}
        >
            {/* Card Header */}
            <div className={`
                px-3 py-2 flex items-center gap-2 border-b
                ${type === 'chapter' ? 'bg-accent/10' : 'bg-muted/50'}
            `}>
                <button
                    {...attributes}
                    {...listeners}
                    className="cursor-grab active:cursor-grabbing p-1 hover:bg-muted rounded"
                    onClick={(e) => e.stopPropagation()}
                >
                    <GripVertical className="h-4 w-4 text-muted-foreground" />
                </button>
                <Badge variant={type === 'chapter' ? 'default' : 'outline'} className="text-xs">
                    {type === 'chapter' ? `Ch ${item.order + 1}` : 'Scene'}
                </Badge>
                <span className="flex-1 font-medium text-sm truncate">{item.title}</span>
            </div>

            {/* Card Body - Synopsis Preview */}
            <div className="p-3 min-h-[80px]">
                {synopsis ? (
                    <p className="text-xs text-muted-foreground line-clamp-4">{synopsis}</p>
                ) : (
                    <p className="text-xs text-muted-foreground/50 italic">No synopsis yet...</p>
                )}
            </div>

            {/* Card Footer */}
            <div className="px-3 py-2 bg-muted/30 flex items-center justify-between text-xs">
                <span className="text-muted-foreground">
                    {wordCount.toLocaleString()} words
                </span>
                <div className="flex items-center gap-1">
                    <button
                        className="p-1 hover:bg-muted rounded text-muted-foreground hover:text-foreground"
                        onClick={(e) => { e.stopPropagation(); onEdit(item); }}
                        title="Edit"
                    >
                        <Edit2 className="h-3 w-3" />
                    </button>
                    <button
                        className="p-1 hover:bg-destructive/10 rounded text-muted-foreground hover:text-destructive"
                        onClick={(e) => { e.stopPropagation(); onDelete(item); }}
                        title="Delete"
                    >
                        <Trash2 className="h-3 w-3" />
                    </button>
                </div>
            </div>
        </div>
    );
};

// Main Corkboard Component
const CorkboardView = ({ 
    bookId, 
    chapters, 
    setChapters,
    scenes,
    setScenes,
    onSelectChapter,
    onSelectScene 
}) => {
    const [viewMode, setViewMode] = useState('chapters'); // 'chapters' or 'all'
    const [selectedItem, setSelectedItem] = useState(null);
    const [editingItem, setEditingItem] = useState(null);
    const [editForm, setEditForm] = useState({ title: '', synopsis: '' });
    const [activeId, setActiveId] = useState(null);

    const sensors = useSensors(
        useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
        useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
    );

    // Get all items for display
    const getDisplayItems = () => {
        if (viewMode === 'chapters') {
            return chapters.map(ch => ({ ...ch, type: 'chapter' }));
        }
        // All view - chapters with their scenes nested
        const items = [];
        chapters.forEach(ch => {
            items.push({ ...ch, type: 'chapter' });
            const chapterScenes = scenes.filter(s => s.chapter_id === ch.id);
            chapterScenes.forEach(sc => {
                items.push({ ...sc, type: 'scene', parentChapter: ch.title });
            });
        });
        return items;
    };

    const handleDragStart = (event) => {
        setActiveId(event.active.id);
    };

    const handleDragEnd = async (event) => {
        const { active, over } = event;
        setActiveId(null);

        if (!over || active.id === over.id) return;

        const items = getDisplayItems();
        const oldIndex = items.findIndex(i => i.id === active.id);
        const newIndex = items.findIndex(i => i.id === over.id);

        if (oldIndex === -1 || newIndex === -1) return;

        const movedItem = items[oldIndex];

        if (movedItem.type === 'chapter') {
            // Reorder chapters
            const newChapters = arrayMove(chapters, 
                chapters.findIndex(c => c.id === active.id),
                chapters.findIndex(c => c.id === over.id)
            );
            
            // Update order property
            const updatedChapters = newChapters.map((ch, idx) => ({ ...ch, order: idx }));
            setChapters(updatedChapters);

            // Save to backend
            try {
                await Promise.all(updatedChapters.map(ch => 
                    chaptersApi.update(ch.id, { order: ch.order })
                ));
                toast.success('Chapter order updated');
            } catch (error) {
                toast.error('Failed to save order');
            }
        }
    };

    const handleSelect = (item) => {
        setSelectedItem(item);
        if (item.type === 'chapter') {
            onSelectChapter(item);
        } else {
            onSelectScene(item);
        }
    };

    const handleEdit = (item) => {
        setEditingItem(item);
        setEditForm({
            title: item.title,
            synopsis: item.synopsis || item.description || ''
        });
    };

    const handleSaveEdit = async () => {
        if (!editingItem) return;

        try {
            if (editingItem.type === 'chapter') {
                await chaptersApi.update(editingItem.id, {
                    title: editForm.title,
                    synopsis: editForm.synopsis
                });
                setChapters(prev => prev.map(ch => 
                    ch.id === editingItem.id 
                        ? { ...ch, title: editForm.title, synopsis: editForm.synopsis }
                        : ch
                ));
            } else {
                await scenesApi.update(editingItem.id, {
                    title: editForm.title,
                    description: editForm.synopsis
                });
                setScenes(prev => prev.map(sc => 
                    sc.id === editingItem.id 
                        ? { ...sc, title: editForm.title, description: editForm.synopsis }
                        : sc
                ));
            }
            toast.success('Updated successfully');
            setEditingItem(null);
        } catch (error) {
            toast.error('Failed to update');
        }
    };

    const handleDelete = async (item) => {
        if (!confirm(`Delete "${item.title}"? This cannot be undone.`)) return;

        try {
            if (item.type === 'chapter') {
                await chaptersApi.delete(item.id);
                setChapters(prev => prev.filter(ch => ch.id !== item.id));
            } else {
                await scenesApi.delete(item.id);
                setScenes(prev => prev.filter(sc => sc.id !== item.id));
            }
            toast.success('Deleted successfully');
        } catch (error) {
            toast.error('Failed to delete');
        }
    };

    const items = getDisplayItems();
    const totalWordCount = chapters.reduce((sum, ch) => sum + (ch.word_count || 0), 0);

    return (
        <div className="p-6">
            {/* Header */}
            <div className="flex items-center justify-between mb-6">
                <div>
                    <h2 className="font-serif text-2xl font-semibold flex items-center gap-2">
                        <LayoutGrid className="h-6 w-6 text-accent" />
                        Corkboard View
                    </h2>
                    <p className="text-muted-foreground mt-1">
                        Drag and drop to rearrange your story structure
                    </p>
                </div>
                <div className="flex items-center gap-4">
                    {/* Total Word Count */}
                    <div className="text-right">
                        <div className="text-xs text-muted-foreground">Total Word Count</div>
                        <div className="text-xl font-semibold">{totalWordCount.toLocaleString()}</div>
                    </div>
                    
                    {/* View Toggle */}
                    <div className="flex rounded-lg border border-input overflow-hidden">
                        <button
                            className={`px-3 py-1.5 text-sm ${viewMode === 'chapters' ? 'bg-accent text-accent-foreground' : 'hover:bg-muted'}`}
                            onClick={() => setViewMode('chapters')}
                        >
                            Chapters
                        </button>
                        <button
                            className={`px-3 py-1.5 text-sm ${viewMode === 'all' ? 'bg-accent text-accent-foreground' : 'hover:bg-muted'}`}
                            onClick={() => setViewMode('all')}
                        >
                            All
                        </button>
                    </div>
                </div>
            </div>

            {/* Instructions */}
            <div className="mb-4 p-3 bg-accent/5 rounded-lg text-sm text-muted-foreground">
                <strong>Tip:</strong> Drag cards by the grip handle to rearrange. Click a card to select and edit. 
                Double-click to open in the editor.
            </div>

            {/* Corkboard Grid */}
            {items.length === 0 ? (
                <div className="text-center py-16 card-default">
                    <LayoutGrid className="h-16 w-16 mx-auto text-muted-foreground/30 mb-4" />
                    <h3 className="font-serif text-lg font-semibold mb-2">No chapters yet</h3>
                    <p className="text-sm text-muted-foreground mb-4">
                        Create your first chapter to start organizing your story
                    </p>
                </div>
            ) : (
                <DndContext
                    sensors={sensors}
                    collisionDetection={closestCenter}
                    onDragStart={handleDragStart}
                    onDragEnd={handleDragEnd}
                >
                    <SortableContext items={items.map(i => i.id)} strategy={rectSortingStrategy}>
                        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
                            {items.map(item => (
                                <CorkboardCard
                                    key={item.id}
                                    item={item}
                                    type={item.type}
                                    onEdit={handleEdit}
                                    onDelete={handleDelete}
                                    onSelect={handleSelect}
                                    isSelected={selectedItem?.id === item.id}
                                />
                            ))}
                        </div>
                    </SortableContext>
                </DndContext>
            )}

            {/* Edit Dialog */}
            <Dialog open={!!editingItem} onOpenChange={(open) => !open && setEditingItem(null)}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>
                            Edit {editingItem?.type === 'chapter' ? 'Chapter' : 'Scene'}
                        </DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 mt-4">
                        <div>
                            <label className="text-sm font-medium">Title</label>
                            <Input
                                value={editForm.title}
                                onChange={(e) => setEditForm({ ...editForm, title: e.target.value })}
                                className="mt-1"
                            />
                        </div>
                        <div>
                            <label className="text-sm font-medium">Synopsis</label>
                            <Textarea
                                value={editForm.synopsis}
                                onChange={(e) => setEditForm({ ...editForm, synopsis: e.target.value })}
                                rows={4}
                                className="mt-1"
                                placeholder="Brief description of what happens in this section..."
                            />
                        </div>
                        <div className="flex justify-end gap-3">
                            <Button variant="outline" onClick={() => setEditingItem(null)}>
                                Cancel
                            </Button>
                            <Button onClick={handleSaveEdit}>
                                Save Changes
                            </Button>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default CorkboardView;
